import React, { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import {
  OPTION_ALL,
  getTypeOptionsWithCounter,
} from '../../common/filters/single-select-filter/utils';
import { configurationTypesOptions } from './utils';
import { SearchResponseWithConfigurationItems, SearchType } from './types';
import uniq from 'lodash/uniq';
import { useSearchState, UseSearchStateReturn } from '../building-blocks/search/useSearchState';
import { SingleSelectFilterPopoverOption } from '../../common/filters/single-select-filter/SingleSelectFilterPopover';
import { useDocumentationApiFacade } from '../../../apis/facades/useDocumentationApiFacade';

type UniversalSearchContextType = UseSearchStateReturn<SearchResponseWithConfigurationItems>;

const UniversalSearchContext = createContext<UniversalSearchContextType | undefined>(undefined);

export interface UniversalSearchProviderProps {
  children: React.ReactNode;
  crmOrgId: string;
  excludeTypes?: string[];
}

const SEARCH_TYPE_OPTIONS: SingleSelectFilterPopoverOption[] = [
  { value: SearchType.apiNameAndLabel, label: 'Search by name' },
  { value: SearchType.content, label: 'Search in metadata' },
];

export const UniversalSearchProvider: React.FC<UniversalSearchProviderProps> = ({
  children,
  crmOrgId,
  excludeTypes = [],
}) => {
  const { get_searchNameAndLabel, get_searchContent } = useDocumentationApiFacade();

  const searchFn = useCallback(
    async (term: string, searchType: string) => {
      if (searchType === SearchType.content) {
        return get_searchContent({ crmOrgId, searchTxt: term });
      }
      return get_searchNameAndLabel({ crmOrgId, searchTxt: term });
    },
    [crmOrgId, get_searchContent, get_searchNameAndLabel],
  );

  const buildFilterOptions = useCallback(
    (
      results: SearchResponseWithConfigurationItems | undefined,
    ): SingleSelectFilterPopoverOption[] => {
      if (!results) return [OPTION_ALL];

      const configurationOptionsValues = configurationTypesOptions.map((item) => item.value);
      const dynamicFilterOptions = Object.keys(results)
        .map((item) => ({ value: item, label: item }))
        .filter((item) => !configurationOptionsValues.includes(item.value));

      const filteredConfigurationOptions = configurationTypesOptions.filter(
        (option) => !excludeTypes.includes(option.value),
      );
      const filteredDynamicOptions = dynamicFilterOptions.filter(
        (option) => !excludeTypes.includes(option.value),
      );

      return [
        OPTION_ALL,
        ...getTypeOptionsWithCounter(
          uniq([...filteredConfigurationOptions, ...filteredDynamicOptions]),
          (type) =>
            results?.[type.value as keyof SearchResponseWithConfigurationItems]?.length ?? 0,
        ),
      ];
    },
    [excludeTypes],
  );

  const filterResults = useCallback(
    (
      results: SearchResponseWithConfigurationItems,
      filterKey: string,
    ): SearchResponseWithConfigurationItems | undefined => {
      let baseResults = results;

      if (excludeTypes.length > 0) {
        baseResults = Object.fromEntries(
          Object.entries(baseResults).filter(([key]) => !excludeTypes.includes(key)),
        ) as SearchResponseWithConfigurationItems;
      }

      if (filterKey && filterKey !== OPTION_ALL.value) {
        return baseResults[filterKey as keyof SearchResponseWithConfigurationItems]
          ? ({
              [filterKey]: baseResults[filterKey as keyof SearchResponseWithConfigurationItems],
            } as SearchResponseWithConfigurationItems)
          : undefined;
      }

      return baseResults;
    },
    [excludeTypes],
  );

  const searchState = useSearchState<SearchResponseWithConfigurationItems>({
    searchFn,
    defaultSearchType: SearchType.apiNameAndLabel,
    searchTypeOptions: SEARCH_TYPE_OPTIONS,
    buildFilterOptions,
    filterResults,
  });

  const { filterKey, onSelectedFilterItem } = searchState;

  useEffect(() => {
    if (filterKey && excludeTypes.includes(filterKey)) {
      onSelectedFilterItem(undefined);
    }
  }, [filterKey, excludeTypes, onSelectedFilterItem]);

  const contextValue: UniversalSearchContextType = useMemo(
    () => searchState,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      searchState.searchTxt,
      searchState.tempSearchTxt,
      searchState.isLoading,
      searchState.results,
      searchState.hasSearched,
      searchState.isSearchListOpen,
      searchState.selectedSearchType,
      searchState.filterKey,
      searchState.filterOptions,
    ],
  );

  return (
    <UniversalSearchContext.Provider value={contextValue}>
      {children}
    </UniversalSearchContext.Provider>
  );
};

export const useUniversalSearchContext = (): UniversalSearchContextType => {
  const context = useContext(UniversalSearchContext);
  if (context === undefined) {
    throw new Error('useUniversalSearchContext must be used within a UniversalSearchProvider');
  }
  return context;
};
